<template>
  <div>
    <section-title content="Configuración de Secciones" has-line />
    <div class="my-4">
      <validation-observer ref="sectionValidator">
        <div class="row">
          <div class="col-12 col-lg-8">
            <div class="">
              <div class="row d-flex align-items-end">
                <div class="col-12 col-md-4">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <b>Nombre de la Sección:</b>
                    <custom-drop-down
                      v-model="model.section"
                      custom-error-msg="El campo es requerido"
                      :disabled="isDragAndDropEnable"
                      :error="errors[0]"
                      fieldkey="id"
                      fieldname="name"
                      :options="availableSections"
                      placeholder="Seleccione"
                    />
                  </validation-provider>
                </div>
                <div v-if="isOther" class="col-12 col-md-3 pl-0">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-input
                      v-model="model.other"
                      class="mt-2"
                      custom-error-msg="El campo es requerido"
                      :error="errors[0]"
                      maxlength="3000"
                      placeholder="Escriba una Sección"
                    />
                  </validation-provider>
                </div>
                <action-button
                  :disabled="isDragAndDropEnable"
                  i="fas fa-file-plus"
                  text="Añadir Sección"
                  variant="success col-12 col-md-2 px-0"
                  @click="add()"
                />
              </div>
            </div>
          </div>
          <div class="col-12 col-lg-4">
            <div
              v-if="input.length"
              class="d-flex justify-content-lg-end mt-lg-4"
            >
              <div class="mr-1">
                <action-button
                  :class="
                    isDragAndDropEnable
                      ? 'custom-reorganize-btn text-white'
                      : 'outline-dark-gray'
                  "
                  i="far fa-sort-alt"
                  :text="
                    isDragAndDropEnable
                      ? 'Finalizar Reorganización'
                      : 'Reorganizar Secciones'
                  "
                  @click="reorganize()"
                />
              </div>
              <div class="mx-1">
                <action-button
                  class="outline-dark-gray"
                  :disabled="isDragAndDropEnable"
                  :i="
                    allExpanded
                      ? 'fal fa-compress-arrows-alt'
                      : 'fal fa-expand-alt'
                  "
                  :text="allExpanded ? 'Colapsar Todo' : 'Expandir Todo'"
                  @click="toggleExpandCollapse"
                />
              </div>
            </div>
          </div>
        </div>
      </validation-observer>
    </div>
    <section-title v-if="input.length" content="Secciones" has-line />
    <div class="col-12">
      <custom-collapse id="question-sections-collapse">
        <draggable
          :disabled="!isDragAndDropEnable"
          force-fallback
          ghost-class="ghost"
          :list="input"
          @end="dragging = false"
          @start="dragging = true"
        >
          <custom-collapse-item
            v-for="(item, key) in input"
            :id="`section-collapse-item-${key + 1}`"
            :key="`section-collapse-item-${key + 1}`"
            :ref="`section-collapse-item-${key + 1}`"
            :auto-expand-collapse-enabled="false"
            button-icon="far fa-times-circle color-red"
            class="my-3 collapse-item section bg-gray"
            :class="{ 'rounded invalid': item.valid === false }"
            :disabled="isReadOnly || isDragAndDropEnable"
            :expanded="item.expanded"
            expanded-icon="fas fa-plus-circle custom-reorganize-btn"
            icon-class-when-button-disabled="fas fa-grip-vertical flex-fill d-flex justify-content-end align-items-center"
            :keep-btn-visible="false"
            parent-id="question-sections-collapse"
            read-only-icon="fas fa-plus-circle color-gray-2"
            :show-icon-when-button-disabled="isDragAndDropEnable"
            :title="item.name"
            @actionButtonClick="quit(item, key)"
            @expandButtonClick="expand(item, key)"
          >
            <template #title-left>
              <span class="title-number-box rounded ml-2 p-1 text-white">
                {{ key + 1 }}
              </span>
            </template>

            <div class="row">
              <div class="section-title col-6">
                <div class="d-inline mr-2 bg-white">
                  <i class="fas fa-list text-orange fa-lg" />
                </div>
                <span class="color-gray-7 font-16 font-weight-bold">
                  Información de Sección
                </span>
              </div>
              <div
                v-if="item.id !== sectionTypes.LOCALITY"
                class="col-6 d-flex justify-content-end"
              >
                <div class="mr-2">
                  <action-button
                    :class="
                      questionsDragEnabled(key)
                        ? 'custom-reorganize-btn text-white'
                        : 'outline-dark-gray'
                    "
                    :disabled="isReadOnly || isDragAndDropEnable"
                    i="far fa-sort-alt"
                    :text="
                      questionsDragEnabled(key)
                        ? 'Finalizar Reorganización'
                        : 'Reorganizar Preguntas'
                    "
                    @click="reorganizeQuestions(key)"
                  />
                </div>
                <div class="mx-1">
                  <action-button
                    class="outline-dark-gray"
                    :disabled="
                      isReadOnly ||
                        isDragAndDropEnable ||
                        questionsDragEnabled(key)
                    "
                    :i="
                      questionsAllExpanded(key)
                        ? 'fal fa-compress-arrows-alt'
                        : 'fal fa-expand-alt'
                    "
                    :text="
                      questionsAllExpanded(key)
                        ? 'Colapsar Todo'
                        : 'Expandir Todo'
                    "
                    @click="toggleQuestionsExpandCollapse(key)"
                  />
                </div>
              </div>
            </div>
            <questions
              :ref="`section-${key}`"
              :input.sync="item.questions"
              :is-locality="item.id === sectionTypes.LOCALITY"
              :is-read-only="isReadOnly || isDragAndDropEnable"
            />
          </custom-collapse-item>
        </draggable>
      </custom-collapse>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';
import { getSectionTypes } from '@/modules/bidQuestionConfig/api.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import draggable from 'vuedraggable';
import Questions from './Questions';

const SECTIONTYPES = {
  LOCALITY: 130,
  OTHER: -1,
};

export default {
  name: 'Sections',
  components: {
    SectionTitle,
    CustomDropDown,
    ActionButton,
    CustomInput,
    CustomCollapse,
    CustomCollapseItem,
    draggable,
    Questions,
  },
  mixins: [alertMixin],
  props: {
    input: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      model: {
        section: null,
        other: null,
      },
      isDragAndDropEnable: false,
      sections: [],
      totalScore: 0,
    };
  },
  computed: {
    draggingInfo() {
      return this.dragging ? 'under drag' : '';
    },
    allExpanded() {
      return this.input.every((item) => item.expanded);
    },
    isOther() {
      return this.model.section?.id === SECTIONTYPES.OTHER;
    },
    sectionTypes() {
      return SECTIONTYPES;
    },
    availableSections() {
      return this.sections.filter(
        (x) => !this.input.map((x) => x.id).includes(x.id)
      );
    },
  },
  async mounted() {
    await this.getAllSections();
  },
  methods: {
    reorganizeQuestions(sectionIndex) {
      const cmp = this.$refs[`section-${sectionIndex}`]?.[0];
      if (cmp?.reorganize) cmp.reorganize();
    },
    getQuestionsCmp(sectionIndex) {
      return this.$refs[`section-${sectionIndex}`]?.[0] || null;
    },
    questionsDragEnabled(sectionIndex) {
      const cmp = this.$refs[`section-${sectionIndex}`]?.[0];
      return !!cmp?.isDragAndDropEnable;
    },
    questionsAllExpanded(sectionIndex) {
      const cmp = this.getQuestionsCmp(sectionIndex);
      return !!cmp?.isAllExpanded;
    },
    toggleQuestionsExpandCollapse(sectionIndex) {
      const cmp = this.$refs[`section-${sectionIndex}`]?.[0];
      if (cmp?.toggleExpandCollapse) cmp.toggleExpandCollapse();
    },
    reorganize() {
      this.isDragAndDropEnable = !this.isDragAndDropEnable;

      this.input.forEach((item, index) => {
        item.expanded = false;

        const q = this.$refs[`section-${index}`]?.[0];
        if (q?.isDragAndDropEnable) q.reorganize();
      });
    },
    toggleExpandCollapse() {
      const expand = !this.allExpanded;
      this.input.forEach((item) => (item.expanded = expand));
    },
    async getAllSections() {
      const { data } = await getSectionTypes();
      this.sections = data;
      this.sections.unshift({ id: -1, name: 'Otro' });
    },
    resetForm() {
      this.$refs.sectionValidator.reset();
      this.model.other = null;
      this.model.section = null;
    },
    async add() {
      let valid = await this.$refs.sectionValidator.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      valid = await this.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Es necesario documentar las secciones agregadas.',
          'error'
        );
        return;
      }

      const sectionName = this.isOther
        ? this.model.other
        : this.model.section.name;

      if (this.input.some((x) => x.name === sectionName)) {
        this.ShowToast(
          'Alerta',
          'No es posible crear otra sección con el mismo nombre.',
          'error'
        );
        return;
      }

      const item = {
        id: this.isOther ? null : this.model.section.id,
        name: sectionName,
        questions: [
          {
            name: null,
            typeId: 0,
            answers: [],
            childQuestions: [],
            valid: true,
            expanded: false,
            location: {
              quantity: null,
              state: null,
              municipality: null,
              addressLine1: null,
              addressLine2: null,
              postalCode: null,
              attachment: null,
            },
          },
        ],
        valid: true,
        expanded: false,
      };
      this.input.push(item);
      this.resetForm();

      this.expand(item, this.input.length - 1);
      this.$emit('add', item);
      this.$emit('add:section', item);
    },
    expandAll(expanded = true) {
      this.input.forEach((x) => (x.expanded = expanded));
    },
    expand(item, key) {
      if (this.isDragAndDropEnable) return;
      this.input
        .filter((x, i) => i !== key)
        .forEach((x) => (x.expanded = false));
      setTimeout(() => (item.expanded = !item.expanded), 100);
    },
    async quit(item, key) {
      if (this.isDragAndDropEnable) return;
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover la sección <strong>${item.name}</strong>, se eliminarán los datos suministrados hasta ahora?`,
        'Si',
        'No'
      );

      if (!answer) return;
      this.input.splice(key, 1);
      this.$emit('quit', item);
      this.$emit('quit:section', item);
    },
    async validate() {
      if (!this.input.length) return true;
      const forms = this.input.map((item, key) => {
        const validator = this.$refs[`section-${key}`][0];
        return validator.validate().then((val) => (item.valid = val));
      });
      await Promise.all(forms);
      return this.input.every((x) => x.valid);
    },
  },
};
</script>

<style scoped lang="scss">
.outline-dark-gray {
  color: #3f5b60;
  background-color: #ffffff;
  border-color: #3f5b60;
}

.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f7f7f7;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.custom-reorganize-btn {
  background-color: #405c64;
}
.section-title > div {
  border: 3px solid white;
  padding: 6px 7px;
  border-radius: 30px;
  -webkit-box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
}
</style>
